// Shared bits for VIP 服务流程 5.0 (phase 1): labels, date helpers and the
// defensive readers used by the CRM status line, the 打卡 form, the follow-up
// bar and the 月度服务报告 pages. Every reader tolerates a missing field — the
// backend for these endpoints ships separately, and an absent value must hide a
// piece of UI rather than break a page.

import { toPacificWallClock } from '../../utils/pacificTime';
import { utcISOToLAInput, laInputToUTCISO, laTodayYMD } from '../../utils/laTime';

// 主管 roles: see every coach's members, approve 联系频率 changes, review reports.
export const SUPERVISOR_ROLES = ['leader_coach', 'supervisor_coach', 'supervisor', 'manager', 'admin', 'super_admin'];

export const rolesOf = (user) => {
  const out = new Set();
  const add = (r) => { const v = String(r || '').toLowerCase(); if (v) out.add(v); };
  add(user && user.role);
  add(user && user.role_name);
  if (user && Array.isArray(user.roles)) user.roles.forEach(add);
  return out;
};
export const isSupervisorUser = (user) => {
  const rs = rolesOf(user);
  return SUPERVISOR_ROLES.some((r) => rs.has(r));
};

// coach_daily_contacts.purpose / method / outcome
export const PURPOSES = [
  ['followup_48h', '48 小时跟进', '48h follow-up'],
  ['first_contact', '首次联系', 'First contact'],
  ['recommendation', '推荐说明', 'Recommendation'],
  ['question_reply', '问题回复', 'Question reply'],
  ['pre_meeting', '见面前沟通', 'Before a meeting'],
  ['cross_call', '交叉电话', 'Cross call'],
  ['post_meeting', '见面后回访', 'After a meeting'],
  ['weekly', '每周回访', 'Weekly check-in'],
  ['other', '其他', 'Other'],
];
export const METHODS = [
  ['phone', '电话', 'Phone'],
  ['voice', '语音', 'Voice'],
  ['video', '视频', 'Video'],
  ['text', '文字', 'Text'],
  ['in_person', '当面', 'In person'],
];
export const OUTCOMES = [
  ['two_way', '已双向沟通', 'Two-way conversation'],
  ['no_reply', '已联系，未回复', 'Reached out, no reply'],
];
export const MEETING_PURPOSES = ['pre_meeting', 'cross_call', 'post_meeting'];
// Legacy coach_daily_contacts.kind, still written so older views keep reading.
export const kindOfMethod = (m) => (m === 'text' ? 'message' : m === 'in_person' ? 'meet' : 'call');
// Screenshot / photo required for EVERY method (2026-10 rule; the backend
// enforces it too). Kept as a function so callers stay unchanged.
export const needsScreenshot = () => true;
// What the upload should show, per method: 通话截图 / 聊天截图 / 见面照片.
export const screenshotHint = (method, isZh = true) => {
  if (method === 'text') return isZh ? '聊天截图' : 'chat screenshot';
  if (method === 'in_person') return isZh ? '见面照片' : 'meeting photo';
  return isZh ? '通话截图' : 'call screenshot';
};
// 下次联系 required when the method is a phone call (backend enforces it too).
export const needsNextContact = (method) => method === 'phone';

const pick = (list, key, isZh) => {
  const row = list.find((x) => x[0] === key);
  return row ? (isZh ? row[1] : row[2]) : '';
};
export const purposeLabel = (k, isZh = true) => pick(PURPOSES, k, isZh);
export const methodLabel = (k, isZh = true) => pick(METHODS, k, isZh);
export const outcomeLabel = (k, isZh = true) => pick(OUTCOMES, k, isZh);

// ── dates ──────────────────────────────────────────────────────────────────
export const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(typeof v === 'string' && /^\d{4}-\d{2}-\d{2} \d/.test(v) ? v.replace(' ', 'T') : v);
  return isNaN(d.getTime()) ? null : d;
};
const pad = (n) => String(n).padStart(2, '0');
// Instants are shown on the Pacific clock (getHours() would be the browser's);
// a bare 'YYYY-MM-DD' (pause_until …) is a calendar date and is shown as is.
const YMD_ONLY = /^\d{4}-\d{2}-\d{2}$/;
export const fmtMD = (v) => {
  if (typeof v === 'string' && YMD_ONLY.test(v)) return `${v.slice(5, 7)}/${v.slice(8, 10)}`;
  const d = toDate(v); if (!d) return '';
  const w = toPacificWallClock(d);
  return `${pad(w.getMonth() + 1)}/${pad(w.getDate())}`;
};
export const fmtMDHM = (v) => {
  const d = toDate(v); if (!d) return '';
  const w = toPacificWallClock(d);
  return `${pad(w.getMonth() + 1)}/${pad(w.getDate())} ${pad(w.getHours())}:${pad(w.getMinutes())}`;
};
// 'YYYY-MM-DDTHH:mm' on the PACIFIC clock, for <input type="datetime-local">
// (read back with laInputToUTCISO) — what a coach types means US Pacific.
export const toLocalInput = (d) => utcISOToLAInput(d.toISOString());
// A calendar Date's own (local) components, 'YYYY-MM-DD'. For "today" use
// laTodayYMD() — the Pacific date, not the browser's.
export const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// A datetime-local value typed as Pacific → the instant (Date), or null.
export const fromLocalInput = (s) => { const iso = laInputToUTCISO(s); return iso ? new Date(iso) : null; };
// 待办 reminder presets, on the Pacific clock: 2h / 48h from now, 明天 10:00,
// 下周二 10:00 (Pacific days, whatever the browser's zone).
export const memoPresetAt = (key) => {
  const now = Date.now();
  if (key === '2h') return new Date(now + 2 * 3600000);
  if (key === '48h') return new Date(now + 48 * 3600000);
  const today = new Date(`${laTodayYMD()}T00:00:00Z`);
  let add = null;
  if (key === 'tomorrow') add = 1;
  if (key === 'tue') add = ((2 - today.getUTCDay() + 7) % 7) || 7;
  if (add == null) return null;
  today.setUTCDate(today.getUTCDate() + add);
  return fromLocalInput(`${today.toISOString().slice(0, 10)}T10:00`);
};
export const hoursSince = (v) => { const d = toDate(v); return d ? Math.max(0, Math.floor((Date.now() - d.getTime()) / 3600000)) : null; };

// 已超 26 小时 / 已超 2 天 3 小时
export const overText = (hours, isZh = true) => {
  const h = Math.max(0, Math.round(Number(hours) || 0));
  if (h < 48) return isZh ? `已超 ${h} 小时` : `${h}h overdue`;
  const d = Math.floor(h / 24); const r = h % 24;
  return isZh ? `已超 ${d} 天${r ? ` ${r} 小时` : ''}` : `${d}d${r ? ` ${r}h` : ''} overdue`;
};

// ── reports ────────────────────────────────────────────────────────────────
export const REPORT_STATUS = {
  // Luxe tones: muted / danger / ink2 / gold text / ok, each on a light tint.
  draft: { zh: '待起草', en: 'To draft', color: '#6E685F', bg: '#EFEAE2' },
  overdue: { zh: '逾期未起草', en: 'Overdue', color: '#9E2B25', bg: 'rgba(158,43,37,.09)' },
  returned: { zh: '已退回', en: 'Returned', color: '#3A3835', bg: '#EFEAE2' },
  submitted: { zh: '待审核', en: 'In review', color: '#8A6D3B', bg: 'rgba(138,109,59,.12)' },
  published: { zh: '已发布', en: 'Published', color: '#3F7D5A', bg: 'rgba(63,125,90,.12)' },
};
export const reportStatusKey = (r) => {
  if (!r) return 'draft';
  const s = r.status || 'draft';
  if (s === 'draft' && (r.overdue || (toDate(r.due_at) && Date.now() > toDate(r.due_at).getTime() + 2 * 86400000))) return 'overdue';
  return REPORT_STATUS[s] ? s : 'draft';
};

// The month a report is "for": the middle of its 30-day period.
export const reportMonth = (r) => {
  const a = toDate(r && r.period_start);
  const b = toDate(r && (r.period_end || r.due_at));
  if (a && b) return new Date((a.getTime() + b.getTime()) / 2);
  return a || b || null;
};
export const ZH_MONTHS = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
export const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthNo = (r) => { const d = reportMonth(r); return d ? d.getMonth() + 1 : null; };
export const nextMonthOf = (r) => { const d = reportMonth(r); return d ? (d.getMonth() + 1) % 12 : null; };

// A stat may come as a plain number or as { count, source_ids }.
export const statVal = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v) || 0;
  if (typeof v === 'object') {
    if (v.count != null) return Number(v.count) || 0;
    if (v.value != null) return Number(v.value) || 0;
    if (Array.isArray(v.source_ids)) return v.source_ids.length;
  }
  return 0;
};
export const STAT_KEYS = [
  ['contacts_two_way', '服务沟通', '与您的服务沟通', '沟通', 'Conversations'],
  ['recommended', '推荐人选', '为您推荐的人选', '推荐', 'Introductions'],
  ['interested', '愿意进一步了解', '您愿意进一步了解', '愿意了解', 'You were interested'],
  ['meetings', '正式见面', '正式见面', '见面', 'Meetings'],
];

// timeline JSON may arrive as a string.
export const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) { try { const x = JSON.parse(v); return Array.isArray(x) ? x : []; } catch (e) { return []; } }
  return [];
};
export const asObject = (v) => {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) { try { const x = JSON.parse(v); return x && typeof x === 'object' ? x : {}; } catch (e) { return {}; } }
  return {};
};

// 下月计划 text → bullet lines.
export const planLines = (s) => String(s || '').split(/\n+/).map((x) => x.replace(/^\s*[·•\-*\d.、)]+\s*/, '').trim()).filter(Boolean);

export const personName = (o, prefix = '') => {
  if (!o) return '';
  const f = o[`${prefix}first_name`] || ''; const l = o[`${prefix}last_name`] || '';
  return `${f} ${l}`.trim();
};
